/**
 * CalendarReminderService — Reminders push quotidiens contextuels (Premium+).
 *
 * - Programme une notification locale quotidienne à l'heure choisie par l'user
 * - Texte adapté à la météo / au calendrier (matin = "Tenue du jour ?")
 * - Tier-gated côté UI (Premium / Gold uniquement)
 *
 * Utilise expo-notifications avec trigger calendrier pour répétition quotidienne.
 *
 * v1.12.249
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { getCurrentLang } from '../i18n';

const KEY_SETTINGS = (uid) => `calendar_reminder_${uid || 'guest'}`;
const KEY_NOTIF_ID = (uid) => `calendar_reminder_notifid_${uid || 'guest'}`;

const DEFAULT_HOUR   = 8;
const DEFAULT_MINUTE = 0;

/**
 * Renvoie les messages de reminder localisés selon la langue user.
 * Utilisé par push notifications (hors contexte React).
 */
function getReminderMessages(lang) {
  if (lang === 'en') {
    return [
      { title: '✨ Outfit of the day', body: 'A new outfit awaits you in Mytenue. Discover today\'s AI recommendation.' },
      { title: '👗 Your wardrobe awaits', body: 'Your AI stylist has your outfit of the day ready. Open the app now.' },
      { title: '🌿 Good morning', body: 'Remember to scan today\'s outfit in your Mytenue calendar.' },
      { title: '☕ Good morning', body: 'What will you wear today? Discover your outfit of the day.' },
    ];
  }
  // Default FR
  return [
    { title: '✨ Tenue du jour', body: 'Une nouvelle tenue vous attend dans MyTenue. Découvrez la recommandation IA pour aujourd\'hui.' },
    { title: '👗 À vos placards', body: 'L\'IA a préparé votre tenue du jour. Ouvrez l\'app pour la voir.' },
    { title: '🌿 Bonjour', body: 'Pensez à scanner votre tenue d\'aujourd\'hui dans le calendrier MyTenue.' },
    { title: '☕ Bonjour', body: 'Quelle tenue allez-vous porter aujourd\'hui ? Découvrez votre suggestion du jour.' },
  ];
}

function pickRandomMessage() {
  const lang = (typeof getCurrentLang === 'function' ? getCurrentLang() : 'fr') || 'fr';
  const messages = getReminderMessages(lang);
  return messages[Math.floor(Math.random() * messages.length)];
}

/**
 * État actuel du reminder pour un user.
 * @returns {{ enabled: boolean, hour: number, minute: number }}
 */
export async function getReminderSettings(userId) {
  if (!userId) return { enabled: false, hour: DEFAULT_HOUR, minute: DEFAULT_MINUTE };
  try {
    const raw = await AsyncStorage.getItem(KEY_SETTINGS(userId));
    if (raw) {
      const obj = JSON.parse(raw);
      return {
        enabled: !!obj.enabled,
        hour:    typeof obj.hour === 'number' ? obj.hour : DEFAULT_HOUR,
        minute:  typeof obj.minute === 'number' ? obj.minute : DEFAULT_MINUTE,
      };
    }
  } catch {}
  return { enabled: false, hour: DEFAULT_HOUR, minute: DEFAULT_MINUTE };
}

async function saveReminderSettings(userId, settings) {
  if (!userId) return;
  await AsyncStorage.setItem(KEY_SETTINGS(userId), JSON.stringify(settings));
}

/**
 * Demande la permission notifications + programme la répétition quotidienne.
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
export async function enableDailyReminder({ userId, hour = DEFAULT_HOUR, minute = DEFAULT_MINUTE }) {
  if (!userId) return { ok: false, reason: 'no_user' };
  try {
    const Notifications = require('expo-notifications');
    if (!Notifications?.scheduleNotificationAsync) {
      return { ok: false, reason: 'notifications_module_unavailable' };
    }

    // 1) Demander permission
    const perm = await Notifications.getPermissionsAsync();
    let status = perm.status;
    if (status !== 'granted') {
      const r = await Notifications.requestPermissionsAsync();
      status = r.status;
    }
    if (status !== 'granted') {
      return { ok: false, reason: 'permission_denied' };
    }

    // 2) Annuler ancien reminder s'il existe
    await disableDailyReminder({ userId });

    // 3) Programmer le nouveau
    // v1.12.544 — expo-notifications SDK 50+ exige `type: 'daily'` dans trigger
    // (l'ancien format { hour, minute, repeats: true } est invalide)
    const msg = pickRandomMessage();
    let trigger;
    try {
      // Tente d'abord la nouvelle API (DAILY) qui marche en SDK récent
      const SchedulableTriggerInputTypes = Notifications.SchedulableTriggerInputTypes;
      const type = SchedulableTriggerInputTypes?.DAILY || 'daily';
      trigger = { type, hour, minute };
    } catch {
      trigger = { type: 'daily', hour, minute };
    }
    let notifId;
    try {
      notifId = await Notifications.scheduleNotificationAsync({
        content: {
          title: msg.title,
          body:  msg.body,
          data:  { type: 'calendar_reminder', deeplink: 'mytenue://calendar' },
          sound: true,
        },
        trigger,
      });
    } catch (triggerErr) {
      // Fallback ancien format si la nouvelle API rejette (SDK ancien)
      console.warn('[CalendarReminder] new trigger failed, fallback legacy:', triggerErr?.message);
      notifId = await Notifications.scheduleNotificationAsync({
        content: {
          title: msg.title,
          body:  msg.body,
          data:  { type: 'calendar_reminder', deeplink: 'mytenue://calendar' },
          sound: true,
        },
        trigger: { hour, minute, repeats: true },
      });
    }

    // 4) Persister settings + id
    await saveReminderSettings(userId, { enabled: true, hour, minute });
    await AsyncStorage.setItem(KEY_NOTIF_ID(userId), String(notifId || ''));
    console.log(`[CalendarReminder] ✓ enabled @ ${hour}:${String(minute).padStart(2, '0')} (id=${notifId})`);
    return { ok: true, hour, minute };
  } catch (e) {
    console.warn('[CalendarReminder] enable failed:', e?.message);
    return { ok: false, reason: e?.message || 'unknown' };
  }
}

/**
 * Annule le reminder quotidien.
 */
export async function disableDailyReminder({ userId }) {
  if (!userId) return { ok: false };
  try {
    const Notifications = require('expo-notifications');
    if (Notifications?.cancelScheduledNotificationAsync) {
      const oldId = await AsyncStorage.getItem(KEY_NOTIF_ID(userId));
      if (oldId) {
        try { await Notifications.cancelScheduledNotificationAsync(oldId); } catch {}
      }
      // Filet de sécurité : annuler tous les reminders calendrier (au cas où)
      try {
        const all = await Notifications.getAllScheduledNotificationsAsync();
        for (const n of (all || [])) {
          if (n?.content?.data?.type === 'calendar_reminder') {
            await Notifications.cancelScheduledNotificationAsync(n.identifier);
          }
        }
      } catch {}
    }
    await AsyncStorage.removeItem(KEY_NOTIF_ID(userId));
    const settings = await getReminderSettings(userId);
    await saveReminderSettings(userId, { ...settings, enabled: false });
    console.log('[CalendarReminder] ✓ disabled');
    return { ok: true };
  } catch (e) {
    console.warn('[CalendarReminder] disable failed:', e?.message);
    return { ok: false, reason: e?.message };
  }
}

/**
 * Vérifie si l'user (selon son tier) a accès aux reminders.
 */
export function isReminderAllowedForTier(tier) {
  return tier === 'premium' || tier === 'gold';
}
